import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const prefixes = [
    'pixel', 'cyber', 'neo', 'quantum', 'cosmic', 'digital', 'nova', 'echo',
    'flux', 'meta', 'hyper', 'neon', 'tech', 'zen', 'void', 'arc', 'pulse'
];

const suffixes = [
    'wave', 'mind', 'sync', 'flux', 'flow', 'spark', 'byte', 'node', 
    'edge', 'core', 'loop', 'link', 'grid', 'verse', 'shift', 'prime'
];

const connectors = ['_', '.', '', 'x', 'v', 'z'];

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [loading, setLoading] = useState(true);

    const generateUsername = () => {
        const prefix = prefixes[Math.floor(Math.random() * prefixes.length)];
        const suffix = suffixes[Math.floor(Math.random() * suffixes.length)];
        const connector = connectors[Math.floor(Math.random() * connectors.length)];
        return `${prefix}${connector}${suffix}`;
    };

    const generateAvatarUrl = () => {
        const seed = Math.random().toString(36).substring(7);
        return `https://api.dicebear.com/7.x/shapes/svg?seed=${seed}&backgroundColor=ffdfbf,ffd5dc,baffc9,bae1ff&backgroundType=gradientLinear&shape1Color=0a5b83,1c7293,065471,1b3b6f,21295c`;
    };

    useEffect(() => {
        try {
            const storedUser = localStorage.getItem('user');
            const storedToken = localStorage.getItem('token');
            
            if (storedUser && storedToken) {
                const parsedUser = JSON.parse(storedUser);
                if (parsedUser) {
                    setCurrentUser(parsedUser);
                    setIsAuthenticated(true);
                    axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
                }
            }
        } catch (error) {
            // Clear potentially corrupted data
            localStorage.removeItem('user');
            localStorage.removeItem('token');
        } finally {
            setLoading(false);
        }
    }, []);

    const signIn = async (userData) => {
        try {

            const baseURL = process.env.REACT_APP_API_URL;
            if (!baseURL) {
                throw new Error('Application configuration error. Please contact support.');
            }

            // For Google Sign In
            if (userData.googleId) {
                // Generate username first and log it
                const generatedUsername = generateUsername();

                const googleSignInData = {
                    email: userData.email,
                    googleId: userData.googleId,
                    username: generatedUsername,
                    firstName: userData.firstName,
                    lastName: userData.lastName,
                    name: userData.name,
                    imageUrl: generateAvatarUrl()
                };

                const response = await axios.post(
                    `${baseURL}/auth/signin`,
                    googleSignInData,
                    {
                        headers: { 
                            'Content-Type': 'application/json'
                        },
                        withCredentials: true,
                        timeout: 5000
                    }
                );

                if (response.data.success && response.data.user && response.data.token) {
                    localStorage.setItem('token', response.data.token);
                    localStorage.setItem('user', JSON.stringify(response.data.user));
                    axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
                    setCurrentUser(response.data.user);
                    setIsAuthenticated(true);
                    return response.data.user;
                }
            } else {
                // Regular email/password signin
                const response = await axios.post(
                    `${baseURL}/auth/signin`,
                    userData,
                    {
                        headers: { 'Content-Type': 'application/json' },
                        withCredentials: true,
                        timeout: 5000
                    }
                );

                if (response.data.success) {
                    const { user, token } = response.data;
                    if (user && token) {
                        localStorage.setItem('token', token);
                        localStorage.setItem('user', JSON.stringify(user));
                        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
                        setCurrentUser(user);
                        setIsAuthenticated(true);
                        return user;
                    }
                }
            }
            
            throw new Error('Unable to authenticate. Please try again.');
        } catch (error) {
            console.error('SignIn error:', error);
            if (error.response?.data) {
                console.error('Server response:', error.response.data);
                throw new Error(error.response.data.message || error.response.data.error || 'Authentication failed');
            }
            throw error;
        }
    };

    const value = {
        currentUser,
        isAuthenticated,
        setIsAuthenticated,
        loading,
        user: currentUser,
        signIn,
        generateUsername,  // Export this if needed elsewhere
        generateAvatarUrl // Export this if needed elsewhere
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}; 